import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { resolveImageUrl } from '../utils/urlResolver';
import { LayoutDashboard, Home } from 'lucide-react';

const features = [
  {
    title: 'Add Magic Changes',
    desc: 'Get fun ideas to make your art even cooler!'
  },
  {
    title: 'Try Art Styles',
    desc: 'See your art in famous styles like Impressionism or Cubism!'
  },
  {
    title: 'Chat with Artmosphere',
    desc: 'Tell Artmosphere what you want to change in your art!'
  }
];

const placeholderArt = 'https://dummyimage.com/400x400/eee/aaa&text=Your+Artwork+Here';

export default function ReimagineArtwork() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);

  // Ensure browser back from this page returns to Magic landing
  useEffect(() => {
    try {
      const onPop = (e) => {
        e.preventDefault();
        try { navigate('/magic', { replace: true }); } catch (_) {}
        window.location.replace('/magic');
      };
      window.addEventListener('popstate', onPop);
      return () => window.removeEventListener('popstate', onPop);
    } catch (_) {}
  }, [navigate]);

  // Load artwork from state or query param
  useEffect(() => {
    const loadArtwork = async () => {
      try {
        setLoading(true);
        console.log('[ReimagineArtwork] Loading artwork from location.state:', location.state);
        console.log('[ReimagineArtwork] Search params:', searchParams.get('artworkId'));
        
        // Try location.state first
        let selectedArtwork = location.state?.artwork;
        
        if (selectedArtwork) {
          console.log('[ReimagineArtwork] Found artwork in location.state:', selectedArtwork);
          setArtwork(selectedArtwork);
          setLoading(false);
          return;
        }
        
        // If not in state, try query param
        const artworkId = searchParams.get('artworkId');
        if (artworkId && user) {
          console.log('[ReimagineArtwork] Fetching artwork from Firestore, ID:', artworkId);
          try {
            // Try artworks collection first
            const artworkDoc = await getDoc(doc(db, 'artworks', artworkId));
            if (artworkDoc.exists()) {
              selectedArtwork = { id: artworkDoc.id, ...artworkDoc.data() };
              console.log('[ReimagineArtwork] Found in artworks collection:', selectedArtwork);
            } else {
              // Try sharedArtworks collection
              const sharedDoc = await getDoc(doc(db, 'sharedArtworks', artworkId));
              if (sharedDoc.exists()) {
                const data = sharedDoc.data();
                selectedArtwork = {
                  id: sharedDoc.id,
                  title: data.artworkTitle || data.title,
                  description: data.artworkDescription || data.description,
                  imageUrl: data.artworkUrl || data.imageUrl,
                  ...data
                };
                console.log('[ReimagineArtwork] Found in sharedArtworks collection:', selectedArtwork);
              }
            }
          } catch (error) {
            console.error('[ReimagineArtwork] Error fetching artwork from query param:', error);
          }
        }
        
        if (selectedArtwork) {
          console.log('[ReimagineArtwork] Setting artwork:', selectedArtwork);
        } else {
          console.warn('[ReimagineArtwork] No artwork found');
        }
        
        setArtwork(selectedArtwork);
      } catch (error) {
        console.error('[ReimagineArtwork] Error loading artwork:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadArtwork();
  }, [location.state, searchParams, user]);

  // Robust artwork selection logic (replicating StoryCreator)
  const artworkUrl = artwork
    ? (artwork.originalArtwork || artwork.imageUrl || artwork.coverUrl || artwork.artworkUrl)
    : (location.state?.artwork?.originalArtwork ||
       location.state?.artwork?.imageUrl ||
       location.state?.artwork?.coverUrl ||
       location.state?.imageUrl ||
       placeholderArt);

  return (
    <div className="min-h-screen flex flex-col bg-[#FFF8E1] px-0 py-0 md:px-[2.5cm] md:py-6">
      <div className="relative z-10 w-full max-w-full flex-1 flex flex-col py-0 md:py-4">
        <div className="bg-white/95 rounded-none md:rounded-[32px] shadow-2xl border-0 md:border-2 border-amber-300 flex flex-col flex-1 min-h-0 overflow-hidden">
          <header className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-b border-amber-200/50 shadow-sm max-h-[72px] md:max-h-[80px]">
            <div className="flex items-center justify-between py-2 md:py-2.5 h-full min-h-[52px] px-3 md:px-6">
              <p className="text-sm md:text-base font-semibold text-gray-800 truncate pr-2">Re-imagine with Artmosphere</p>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button type="button" onClick={() => navigate('/my-art-world')} className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center" title="Artist Corner">
                  <LayoutDashboard className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                  <span className="hidden md:inline ml-1">Artist Corner</span>
                </button>
                <button type="button" onClick={() => navigate('/home')} className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center" title="Home">
                  <Home className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                  <span className="hidden md:inline ml-1">Home</span>
                </button>
                <button type="button" onClick={() => navigate('/activity-dashboard?view=magic-studio', { state: { view: 'magic-studio' } })} className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center" title="My Activities">My Activities</button>
              </div>
            </div>
          </header>
          <div className="flex-1 min-h-0 overflow-auto max-w-6xl mx-auto w-full p-3 md:p-6">
        <p className="text-amber-700 text-center mb-6 text-sm md:text-base" style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}>Welcome! Let&apos;s reimagine your artwork together with Artmosphere.</p>
        {/* Main Two-Column Layout */}
        <div className="flex flex-col md:flex-row gap-8">
          {/* Left: Artwork */}
          <div className="w-full md:w-[500px] flex-shrink-0 flex items-center justify-center">
            <div className="relative w-full h-[500px] bg-white/80 backdrop-blur-sm border-2 border-amber-200 rounded-2xl shadow-lg overflow-hidden">
              <img
                src={resolveImageUrl(artworkUrl)}
                alt={artwork?.title || "Selected Artwork"}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = placeholderArt;
                }}
              />
              <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded">Your Artwork</div>
            </div>
          </div>
          {/* Right: Features and CTA */}
          <div className="flex-1 flex flex-col justify-between p-10">
            <div>
              <h1 className="text-4xl font-bold text-amber-800 mb-3" style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}>Re-imagine with Artmosphere</h1>
              <p className="text-lg text-amber-700 mb-8" style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}>
                Give your artwork a magical twist! Add new colors, characters, and styles. Learn about famous art movements and see your art transform—side by side!
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-10">
                {features.map((f, i) => (
                  <div key={i} className="flex items-center gap-4 bg-amber-50 rounded-xl p-4 shadow hover:shadow-lg transition-all border border-amber-100">
                    <div>
                      <div className="font-bold text-amber-800 text-lg mb-1" style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}>{f.title}</div>
                      <div className="text-amber-700 text-base" style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}>{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* You can add a CTA button or more features here if needed */}
          </div>
        </div>
          </div>
        </div>
      </div>
    </div>
  );
} 